import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { User, Sparkles } from 'lucide-react';

const NicknameSetup = ({ user, onComplete }) => {
  const [nickname, setNickname] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    const trimmedNickname = nickname.trim();
    if (trimmedNickname.length < 2 || trimmedNickname.length > 20) {
      setError('Nickname must be 2-20 characters');
      return;
    }

    setIsLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const sessionToken = localStorage.getItem('session_token');
      const headers = {
        'Content-Type': 'application/json',
      };
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }
      
      const response = await fetch(`${backendUrl}/api/auth/nickname`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ nickname: trimmedNickname }),
        credentials: 'include',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to set nickname');
      }

      const updatedUser = await response.json();
      onComplete(updatedUser);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 max-w-md w-full text-center">
        <div className="mb-6">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-full h-full rounded-full" />
            ) : (
              <User className="w-10 h-10 text-white" />
            )}
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Welcome, {user?.name}!</h2>
          <p className="text-gray-300">Choose a nickname for the leaderboard</p>
          <p className="text-gray-400 text-sm mt-1">This will be shown publicly (not your real name)</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <Input
              type="text"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="Enter your nickname"
              className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 text-center text-lg py-6"
              maxLength={20}
              autoFocus
            />
            <Sparkles className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-yellow-400" />
          </div>
          
          {error && (
            <p className="text-red-400 text-sm">{error}</p>
          )}
          
          <p className="text-gray-400 text-sm">
            {nickname.length}/20 characters
          </p>

          <Button
            type="submit"
            disabled={isLoading || nickname.trim().length < 2}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-6 text-lg"
          >
            {isLoading ? 'Setting up...' : 'Start Playing!'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default NicknameSetup;
